import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { supabase } from '@/lib/supabaseClient';
import { DocumentMetadata, DocumentVersion, User } from '@/types';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Download, FileText, UserCircle, CalendarDays } from 'lucide-react';
import { format } from 'date-fns';
import { Skeleton } from '@/components/ui/skeleton';

interface VersionHistoryDialogProps {
  isOpen: boolean;
  onClose: () => void;
  document: (DocumentMetadata & { projectId?: string; claimId?: string; versionNumber?: number; version?: number; versions?: DocumentVersion[] }) | null;
}

interface EnrichedDocumentVersion extends DocumentVersion {
  uploaderName?: string;
}

interface DocumentWithVersions extends DocumentMetadata {
    versions: DocumentVersion[];
    projectId?: string;
    claimId?: string;
}

export default function VersionHistoryDialog({ isOpen, onClose, document: documentProp }: VersionHistoryDialogProps) {
  const { toast } = useToast();
  const [versions, setVersions] = useState<EnrichedDocumentVersion[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchVersions = async () => {
      if (!documentProp || !documentProp.documentMetadataId) {
        setVersions([]);
        return;
      }
      setIsLoading(true);
      try {
        let fetchedVersions: DocumentVersion[] = documentProp.versions || [];

        if (!documentProp.versions || documentProp.versions.length === 0) {
            let parentEntityEndpoint = '';
            if (documentProp.projectId) {
                parentEntityEndpoint = `/api/projects/${documentProp.projectId}/documents`;
            } else if (documentProp.claimId) { 
                parentEntityEndpoint = `/api/claims/${documentProp.claimId}/documents`;
            }

            if (parentEntityEndpoint) {
                const response = await apiRequest('GET', parentEntityEndpoint);
                const allDocsInParent: DocumentWithVersions[] = await response.json();
                const targetDoc = allDocsInParent.find((d) => d.documentMetadataId === documentProp.documentMetadataId);
                if (targetDoc && targetDoc.versions) {
                    fetchedVersions = targetDoc.versions;
                }
            }
        }

        const enrichedVersions = await Promise.all(
          fetchedVersions.map(async (version) => {
            let uploaderName = 'Unknown User';
            try {
              const userResponse = await apiRequest('GET', `/api/users/${version.uploadedByUserId}`);
              if (userResponse.ok) {
                const userData: User = await userResponse.json();
                uploaderName = userData.fullName || userData.email || 'Unknown User';
              }
            } catch (userError) {
              console.warn(`Failed to fetch user ${version.uploadedByUserId}`, userError);
            }
            return { ...version, uploaderName };
          })
        );
        setVersions(enrichedVersions.sort((a, b) => b.versionNumber - a.versionNumber));
      } catch (error) {
        console.error('Failed to fetch version history:', error);
        toast({
          title: 'Error',
          description: 'Could not load version history.',
          variant: 'destructive',
        });
        setVersions([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      fetchVersions();
    }
  }, [isOpen, documentProp, toast]);

  const handleDownloadVersion = async (version: DocumentVersion) => {
    if (!documentProp) return;
    try {
        const { data, error } = await supabase.storage.from('documents').download(version.filePath);
        if (error) throw error;
        
        const blob = data;
        const link = window.document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${documentProp.fileName} (v${version.versionNumber}).${version.fileType || 'bin'}`;
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
        URL.revokeObjectURL(link.href);

      toast({ title: 'Download Started', description: `Downloading version ${version.versionNumber} of ${documentProp.fileName}` });
    } catch (error) {
      console.error('Download error:', error);
      toast({ title: 'Download Failed', description: 'Could not download this version.', variant: 'destructive' });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] md:max-w-[750px] lg:max-w-[900px]">
        <DialogHeader>
          <DialogTitle>Version History: {documentProp?.fileName}</DialogTitle>
          {documentProp && <DialogDescription>Current Version: {documentProp.versionNumber || documentProp.version || 'N/A'}</DialogDescription>}
        </DialogHeader>
        <div className="mt-4 max-h-[60vh] overflow-y-auto">
          {isLoading ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => 
                <Skeleton key={i} className="h-12 w-full" />
              )}
            </div>
          ) : versions.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Version</TableHead>
                  <TableHead>Uploaded By</TableHead>
                  <TableHead>Upload Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {versions.map((version) => (
                  <TableRow key={version.versionId}>
                    <TableCell className="font-medium">v{version.versionNumber}</TableCell>
                    <TableCell>
                        <div className="flex items-center">
                            <UserCircle className="mr-2 h-4 w-4 text-gray-500" />
                            {version.uploaderName}
                        </div>
                    </TableCell>
                    <TableCell>
                        <div className="flex items-center">
                            <CalendarDays className="mr-2 h-4 w-4 text-gray-500" />
                            {format(new Date(version.uploadTimestamp), 'PPpp')}
                        </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="outline" size="sm" onClick={() => handleDownloadVersion(version)}>
                        <Download className="mr-2 h-4 w-4" /> Download
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="flex flex-col items-center justify-center py-10 text-center">
                <FileText className="w-16 h-16 text-gray-300 mb-4" />
                <p className="text-gray-500">No version history available for this document.</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
} 